import { KYC_DOCUMENTS_TYPES } from '@shinetools/kyc-validation';

import { KycReviewDocumentType } from '__generated__/GQL';
import { apolloClient } from 'common/graphql/clients';
import logger from 'helpers/logger';

import {
  AddressSchema,
  getDefaultAddress,
} from '../../../components/AddressForm/addressSchema';
import { VerificationAddressCreateKycDocumentDocument } from '../graphql/mutations/create-kyc-document.gql';
import { DeleteKycDocumentDocument } from '../graphql/mutations/delete-kyc-document.gql';
import { VerificationAddressUpdatePersonalAddressDocument } from '../graphql/mutations/update-personal-address.gql';
import { GetVerificationAddressInitialDataDocument } from '../graphql/queries/get-initial-data.gql';
import { VerificationAddressGetKycDocumentsDocument } from '../graphql/queries/get-kyc-documents.gql';
import { Context } from './model';

const services = {
  createKycDocument: ({ documentFile, getModuleData }: Context) => {
    const { companyUser } = getModuleData().viewer;

    if (!documentFile) {
      throw new Error('documentFile should be defined');
    }

    return apolloClient.mutate({
      awaitRefetchQueries: true,

      mutation: VerificationAddressCreateKycDocumentDocument,

      // This make sure the kycDocuments key of the Apollo cache is correctly updated
      refetchQueries: [
        {
          query: VerificationAddressGetKycDocumentsDocument,
          variables: {
            companyProfileId: companyUser.companyProfileId,
            types: KYC_DOCUMENTS_TYPES,
          },
        },
      ],
      variables: {
        input: {
          companyUserId: companyUser.companyUserId,
          firebaseBucketName: documentFile.firebaseBucket,
          firebaseFileRef: documentFile.firebaseRef,
          type: documentFile.type as KycReviewDocumentType,
        },
      },
    });
  },

  deleteKycDocument: (
    { getModuleData }: Context,
    event: { kycDocumentId: string },
  ) => {
    const { companyProfileId } = getModuleData().viewer.companyUser;
    const { kycDocumentId } = event;

    return apolloClient.mutate({
      awaitRefetchQueries: true,

      mutation: DeleteKycDocumentDocument,

      // This make sure the kycDocuments key of the Apollo cache is correctly updated
      refetchQueries: [
        {
          query: VerificationAddressGetKycDocumentsDocument,
          variables: {
            companyProfileId,
            types: KYC_DOCUMENTS_TYPES,
          },
        },
      ],
      variables: {
        input: { kycReviewDocumentId: kycDocumentId },
      },
    });
  },

  loadInitialData: async (): Promise<{ address: AddressSchema }> => {
    const { data } = await apolloClient.query({
      query: GetVerificationAddressInitialDataDocument,
    });
    const address = data.viewer.profile;

    try {
      return {
        address: address
          ? {
              city: address.city ?? '',
              street: address.street ?? '',
              streetAddition: address.streetAddition ?? '',
              zip: address.zip ?? '',
            }
          : getDefaultAddress(),
      };
    } catch (e) {
      logger.error(e);

      return { address: getDefaultAddress() };
    }
  },

  sendAddress: async (context: Context): Promise<AddressSchema> => {
    const res = await apolloClient.mutate({
      mutation: VerificationAddressUpdatePersonalAddressDocument,
      variables: { profile: context.address },
    });

    const address = res.data?.updateUserProfile?.profile;

    if (!address) {
      throw new Error();
    }

    return {
      city: address.city ?? '',
      street: address.street ?? '',
      streetAddition: address.streetAddition ?? '',
      zip: address.zip ?? '',
    };
  },
};

export default services;

export type Services = typeof services;
